import {Bin as DeleteIcon} from "iconoir-react";
import {useState} from "react";
import {Navbar} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Modal from "react-bootstrap/Modal";
import Nav from "react-bootstrap/Nav";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useTranslation} from "react-i18next";
import {ClearSelection} from "../common/ClearSelection";
import {DialogCloseCause} from "../common/Enums";
import {InvertSelection} from "../common/InvertSelection";
import {Truncate} from "../common/Truncate";
import {StringUtils} from "../utils/StringUtils";
import {UserPropertyProps} from "./UserProperty";

/**
 * The owner should handle the OnClose event. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause: DialogCloseCause, value?: string) => void;

interface AllowedRecipientsEditorPopupProps {
    defaultValue?: string
    readOnly: boolean
    userPropertyProps: UserPropertyProps
    onClose: OnClose
}

export const AllowedRecipientsEditorPopup = ({
                                                 defaultValue,
                                                 readOnly,
                                                 userPropertyProps,
                                                 onClose
                                             }: AllowedRecipientsEditorPopupProps) =>
{
    /**
     * All allowed recipients
     */
    const [allowedRecipients, setAllowedRecipients] = useState(defaultValue !== undefined && defaultValue !== null ?
        JSON.parse(defaultValue)["ok"].map((r: { toString: () => string; }) => r.toString().toLowerCase())
        : []);
    /**
     * All reject recipients
     */
    const [rejectedRecipients, setRejectedRecipients] = useState(defaultValue !== undefined && defaultValue !== null ?
        JSON.parse(defaultValue)["reject"].map((r: { toString: () => string; }) => r.toString().toLowerCase())
        : []);
    /**
     * The selected allowed recipient
     */
    const [selectedAllowedRecipients, setSelectedAllowedRecipients] = useState<Array<string>>([]);
    /**
     * The selected reject recipient
     */
    const [selectedRejectedRecipients, setSelectedRejectedRecipients] = useState<Array<string>>([]);
    /**
     * New allowed recipient to add
     */
    const [newAllowedRecipient, setNewAllowedRecipient] = useState<string>("");
    /**
     * New rejected recipient to add
     */
    const [newRejectedRecipient, setNewRejectedRecipient] = useState<string>("");
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();
    const {t: tu} = useTranslation('userproperties');

    // called when the dialog is closed without clicking apply
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const apply = () =>
    {
        onClose(DialogCloseCause.Succes, JSON.stringify({
            ok: allowedRecipients,
            reject: rejectedRecipients
        }))
    }

    const invertAllowedRecipientsSelection = () => {
        setSelectedAllowedRecipients([...allowedRecipients].filter(r => !selectedAllowedRecipients.includes(r)));
    }

    const onSelectAllowedRecipient = (checked : boolean, recipient : string) =>
    {
        setSelectedAllowedRecipients(checked ?
            [...selectedAllowedRecipients, recipient] :
            selectedAllowedRecipients.filter(r => r !== recipient)
        );
    }

    const invertRejectRecipientsSelection = () => {
        setSelectedRejectedRecipients([...rejectedRecipients].filter(r => !selectedRejectedRecipients.includes(r)));
    }

    const onSelectRejectedRecipient = (checked : boolean, recipient : string) =>
    {
        setSelectedRejectedRecipients(checked ?
            [...selectedRejectedRecipients, recipient] :
            selectedRejectedRecipients.filter(r => r !== recipient)
        );
    }

    const addAllowedRecipient = () =>
    {
        if (!allowedRecipients.includes(newAllowedRecipient.toLowerCase()))
        {
            setAllowedRecipients([...allowedRecipients, newAllowedRecipient.toLowerCase()]);
            setNewAllowedRecipient("");
        }
    }

    const addRejectedRecipient = () =>
    {
        if (!rejectedRecipients.includes(newRejectedRecipient.toLowerCase()))
        {
            setRejectedRecipients([...rejectedRecipients, newRejectedRecipient.toLowerCase()]);
            setNewRejectedRecipient("");
        }
    }

    return (
        <Modal
            show={true}
            onHide={() => onHideDialog()}
            size="xl"
            fullscreen="lg-down"
            animation={false}
            centered={true}
            scrollable={true}
            contentClassName={"user-property-modal-dialog"}
        >
            <Modal.Header closeButton>
                <Modal.Title>{t(readOnly ? 'user-property.view' : 'user-property.edit')} {tu("userproperty." + userPropertyProps.propertyDetails.userPropertyDetails.name)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col>
                        <h2 className="h4 text-secondary">{t('allowed-recipients.allowed-list')}</h2>

                        <Table bordered hover>
                            <thead>
                            <tr>
                                {selectedAllowedRecipients.length === 0 ?
                                    <>
                                        {!readOnly &&
                                            <th className="th-w-min"> <InvertSelection invertSelectionEvent={invertAllowedRecipientsSelection}/></th>
                                        }
                                        <th>{t("allowed-recipients.recipient-th-title")}</th>
                                    </>
                                    :
                                    <th colSpan={2}>
                                        <Navbar className="bg-body-tertiary ps-2">
                                            {/* Add icon to first column which will clear the selection when clicked */}
                                            <ClearSelection clearSelectionEvent={() => setSelectedAllowedRecipients([])}
                                                            checked={true}/>

                                            <Navbar.Text
                                                className="ms-2">{selectedAllowedRecipients.length} selected</Navbar.Text>

                                            <div className="vr ms-4 me-2"></div>

                                            <Nav>
                                                <Nav.Link onClick={() => {
                                                    setAllowedRecipients([...allowedRecipients].filter(r => !selectedAllowedRecipients.includes(r)));
                                                    setSelectedAllowedRecipients([])
                                                }}>
                                                    <DeleteIcon
                                                        className="text-danger"/> {t("allowed-recipients.remove-selected")}
                                                </Nav.Link>
                                            </Nav>
                                        </Navbar>
                                    </th>
                                }
                            </tr>
                            </thead>
                            <tbody>
                            {
                                allowedRecipients.map((recipient: string) => (
                                    <tr key={recipient} className="text-nowrap cursor-pointer">
                                        {!readOnly &&
                                            <td className="td-w-min">
                                                <Form.Check
                                                    type="checkbox"
                                                    className="me-0"
                                                    checked={selectedAllowedRecipients.includes(recipient)}
                                                    onChange={(e) => onSelectAllowedRecipient(e.target.checked, recipient)}
                                                    inline
                                                />
                                            </td>
                                        }
                                        <td className={"user-select-all"}><Truncate value={recipient} maxLength={60}/></td>
                                    </tr>
                                ))
                            }
                            </tbody>
                        </Table>
                    </Col>
                    {!readOnly &&
                    <Row>
                        <Col md={12} lg={8}>
                            <InputGroup className="mt-2">
                                <Form.Control
                                    placeholder={t("allowed-recipients.new-allowed-recipient")}
                                    value={newAllowedRecipient}
                                    onChange={e => setNewAllowedRecipient(e.target.value)}
                                />

                                <Button variant="primary"
                                        onClick={() => addAllowedRecipient()}
                                        disabled={StringUtils.isBlank(newAllowedRecipient) || allowedRecipients.includes(newAllowedRecipient.toLowerCase())}>
                                    Add
                                </Button>
                            </InputGroup>
                        </Col>
                    </Row>}
                </Row>

                <Row className="mt-4">
                    <Col>
                        <h2 className="h4 text-secondary">{t('allowed-recipients.rejected-list')}</h2>

                        <Table bordered hover>
                            <thead>
                            <tr>
                                {selectedRejectedRecipients.length === 0 ?
                                    <>
                                        {!readOnly &&
                                            <th className="th-w-min"> <InvertSelection invertSelectionEvent={invertRejectRecipientsSelection}/></th>
                                        }
                                        <th>{t("allowed-recipients.recipient-th-title")}</th>
                                    </>
                                    :
                                    <th colSpan={2}>
                                        <Navbar className="bg-body-tertiary ps-2">
                                            {/* Add icon to first column which will clear the selection when clicked */}
                                            <ClearSelection clearSelectionEvent={() => setSelectedRejectedRecipients([])}
                                                            checked={true}/>

                                            <Navbar.Text
                                                className="ms-2">{selectedRejectedRecipients.length} selected</Navbar.Text>

                                            <div className="vr ms-4 me-2"></div>

                                            <Nav>
                                                <Nav.Link onClick={() => {
                                                    setRejectedRecipients([...rejectedRecipients].filter(r => !selectedRejectedRecipients.includes(r)));
                                                    setRejectedRecipients([])
                                                }}>
                                                    <DeleteIcon
                                                        className="text-danger"/> {t("allowed-recipients.remove-selected")}
                                                </Nav.Link>
                                            </Nav>
                                        </Navbar>
                                    </th>
                                }
                            </tr>
                            </thead>
                            <tbody>
                            {
                                rejectedRecipients.map((recipient: string) => (
                                    <tr key={recipient} className="text-nowrap cursor-pointer">
                                        {!readOnly &&
                                            <td className="td-w-min">
                                                <Form.Check
                                                    type="checkbox"
                                                    className="me-0"
                                                    checked={selectedRejectedRecipients.includes(recipient??"")}
                                                    onChange={(e) => onSelectRejectedRecipient(e.target.checked, recipient)}
                                                    inline
                                                />
                                            </td>
                                        }
                                        <td className={"user-select-all"}><Truncate value={recipient} maxLength={60}/></td>
                                    </tr>
                                ))
                            }
                            </tbody>
                        </Table>
                    </Col>
                    {!readOnly &&
                    <Row>
                        <Col md={12} lg={8}>
                            <InputGroup className="mt-2">
                                <Form.Control
                                    placeholder={t("allowed-recipients.new-rejected-recipient")}
                                    value={newRejectedRecipient}
                                    onChange={e => setNewRejectedRecipient(e.target.value)}
                                />

                                <Button variant="primary"
                                        onClick={() => addRejectedRecipient()}
                                        disabled={StringUtils.isBlank(newRejectedRecipient) || rejectedRecipients.includes(newRejectedRecipient.toLowerCase())}>
                                    Add
                                </Button>
                            </InputGroup>
                        </Col>
                    </Row>}
                </Row>
            </Modal.Body>

            {!readOnly &&
                <Modal.Footer>
                    <Button variant="success" onClick={() => apply()}>
                        {t('user-property.apply')}
                    </Button>
                </Modal.Footer>
            }
        </Modal>
    )
}
